import React, { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import intl from "react-intl-universal";
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import { MdCheckCircleOutline, MdErrorOutline } from "react-icons/md";
import { MyFirstAccessContext } from "..";
import { FirstAccessShell } from "../FirstAccessShell";
import {
  getFirstAccessErrorMessage,
  isFirstAccessFailure,
} from "../types";
import { getPasswordRules } from "../utils";

export const ChangePassword: React.FC = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { username, fromPasswordReset, changePassword } = useContext(MyFirstAccessContext);

  const [password, setPassword] = useState("");
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const [transactionalPassword, setTransactionalPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectTimerRef = useRef<number | undefined>(undefined);

  const rules = useMemo(
    () =>
      getPasswordRules({
        password,
        confirmationPassword,
        transactionalPassword,
      }),
    [password, confirmationPassword, transactionalPassword]
  );

  const isInvalid = useMemo(() => rules.some(([passed]) => !passed), [rules]);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    },
    []
  );

  if (!username) {
    return (
      <Redirect
        to={fromPasswordReset ? "/auth/forgot-password" : "/auth/my-first-access"}
      />
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isInvalid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const result = await changePassword(
      username,
      password,
      transactionalPassword
    );

    if (isFirstAccessFailure(result)) {
      setIsSubmitting(false);
      enqueueSnackbar(
        getFirstAccessErrorMessage(
          result,
          intl.get("firstAccess.changePasswordError")
        ),
        { variant: "error" }
      );
      return;
    }

    enqueueSnackbar(intl.get("firstAccess.changePasswordSuccess"), {
      variant: "success",
    });

    redirectTimerRef.current = window.setTimeout(() => {
      history.push("/auth");
    }, 3000);
  };

  const renderPasswordField = ({
    id,
    value,
    onChange,
    placeholder,
    show,
    onToggle,
  }: {
    id: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
    show: boolean;
    onToggle: () => void;
  }) => (
    <div className="login-field login-field--password">
      <span className="login-field__icon">
        <FiLock />
      </span>
      <input
        id={id}
        name={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="login-field__input login-field__input--password"
        autoComplete="new-password"
      />
      <button
        type="button"
        className="login-field__toggle"
        onClick={onToggle}
        onMouseDown={(event) => event.preventDefault()}
        aria-label={
          show ? intl.get("auth.hidePassword") : intl.get("auth.showPassword")
        }
      >
        {show ? <FiEyeOff /> : <FiEye />}
      </button>
    </div>
  );

  return (
    <FirstAccessShell
      title={intl.get("firstAccess.setPasswordTitle")}
      subtitle={intl.get("firstAccess.setPasswordSubtitle")}
    >
      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-field-group">
          <label className="login-field-label" htmlFor="first-access-password">
            {intl.get("firstAccess.accessPassword")}
          </label>
          {renderPasswordField({
            id: "first-access-password",
            value: password,
            onChange: (event) => setPassword(event.currentTarget.value),
            placeholder: intl.get("firstAccess.accessPassword"),
            show: showPassword,
            onToggle: () => setShowPassword((prev) => !prev),
          })}
        </div>

        <div className="login-field-group">
          <label
            className="login-field-label"
            htmlFor="first-access-confirm-password"
          >
            {intl.get("firstAccess.confirmPassword")}
          </label>
          {renderPasswordField({
            id: "first-access-confirm-password",
            value: confirmationPassword,
            onChange: (event) =>
              setConfirmationPassword(event.currentTarget.value),
            placeholder: intl.get("firstAccess.confirmPassword"),
            show: showConfirmation,
            onToggle: () => setShowConfirmation((prev) => !prev),
          })}
        </div>

        <div className="login-field-group">
          <label
            className="login-field-label"
            htmlFor="first-access-transactional-password"
          >
            {intl.get("firstAccess.transactionalPassword")}
          </label>
          <div className="login-field">
            <span className="login-field__icon">
              <FiLock />
            </span>
            <input
              id="first-access-transactional-password"
              name="transactionalPassword"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={transactionalPassword}
              onChange={(event) =>
                setTransactionalPassword(
                  event.currentTarget.value.replace(/\D/g, "")
                )
              }
              placeholder={intl.get("firstAccess.transactionalPassword")}
              className="login-field__input"
              autoComplete="off"
            />
          </div>
        </div>

        <ul className="first-access-password-rules">
          {rules.map(([passed, message]) => (
            <li
              key={message}
              className={
                passed
                  ? "first-access-password-rules__item first-access-password-rules__item--valid"
                  : "first-access-password-rules__item"
              }
            >
              {passed ? <MdCheckCircleOutline /> : <MdErrorOutline />}
              <span>{message}</span>
            </li>
          ))}
        </ul>

        <button
          type="submit"
          className="login-submit-btn"
          disabled={isInvalid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="login-submit-btn__spinner" />
              {intl.get("firstAccess.resettingPassword")}
            </>
          ) : (
            intl.get("firstAccess.resetPassword")
          )}
        </button>
      </form>
    </FirstAccessShell>
  );
};
